import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDateTime, errMsg } from "@/lib/api";
import { Empty, Skeletons, StatusPill } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, ArrowUpRight, ListChecks, CheckCircle2, AlertTriangle } from "lucide-react";

export default function Summaries() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: meetings } = await api.get("/meetings");
        const withSummary = meetings.filter((m) => m.has_summary);
        const details = await Promise.all(
          withSummary.map((m) => api.get(`/meetings/${m.id}/summary`).then(({ data }) => ({ ...data, meeting: m })).catch(() => null))
        );
        setRows(details.filter(Boolean));
        const { data: ai } = await api.get("/action-items");
        setItems(ai);
      } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Skeletons rows={3} className="h-40" />;

  return (
    <div className="space-y-7" data-testid="summaries-page">
      <div>
        <p className="label-xs">Intelijen Rapat</p>
        <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">AI Summary</h1>
        <p className="mt-2 text-sm text-slate-500">{rows.length} ringkasan otomatis · {items.length} action items terekstraksi.</p>
      </div>

      {rows.length === 0 ? (
        <div className="clay">
          <Empty icon={Sparkles} title="Belum ada ringkasan AI"
            sub="Buka detail rapat yang sudah memiliki transkrip, lalu jalankan Generate AI Summary."
            action={<Link to="/meetings"><Button className="rounded-xl grad-brand text-white">Ke daftar rapat</Button></Link>} />
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2" data-testid="summaries-grid">
          {rows.map((r, i) => {
            const own = items.filter((x) => x.meeting_id === r.meeting_id);
            return (
              <div key={r.id} className="clay clay-hover p-7 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`summary-card-${r.meeting_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <StatusPill status={r.meeting.status} />
                    <h3 className="mt-3 font-display text-xl font-semibold leading-snug text-slate-900">{r.meeting.title}</h3>
                    <p className="mt-1.5 text-xs text-slate-500">{fmtDateTime(r.created_at)} · <span className="font-mono">{r.model}</span></p>
                  </div>
                  <Link to={`/meetings/${r.meeting_id}`}>
                    <Button variant="ghost" size="sm" className="text-brand-600" data-testid={`open-summary-${r.meeting_id}`}><ArrowUpRight size={16} /></Button>
                  </Link>
                </div>
                <p className="mt-5 text-sm leading-relaxed text-slate-700 line-clamp-4">{r.executive_summary}</p>
                <div className="mt-6 grid grid-cols-3 gap-4 border-t border-slate-100 pt-4">
                  <div><div className="flex items-center gap-1.5 text-slate-400"><CheckCircle2 size={13} /><span className="label-xs">Keputusan</span></div><p className="mt-1 font-display text-lg font-semibold text-slate-900">{r.decisions?.length || 0}</p></div>
                  <div><div className="flex items-center gap-1.5 text-slate-400"><AlertTriangle size={13} /><span className="label-xs">Risiko</span></div><p className="mt-1 font-display text-lg font-semibold text-slate-900">{r.risks?.length || 0}</p></div>
                  <div><div className="flex items-center gap-1.5 text-slate-400"><ListChecks size={13} /><span className="label-xs">Actions</span></div><p className="mt-1 font-display text-lg font-semibold text-slate-900">{own.length}</p></div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {items.length > 0 && (
        <div className="clay p-6" data-testid="all-action-items">
          <p className="label-xs">Semua Action Items</p>
          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((it) => (
              <Link key={it.id} to={`/meetings/${it.meeting_id}`} className="rounded-2xl border border-slate-200/70 p-5 hover:shadow-clay transition-shadow" data-testid={`global-action-${it.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-semibold leading-snug text-slate-800">{it.task}</p>
                  <StatusPill status={it.priority} />
                </div>
                <p className="mt-3 truncate text-xs text-slate-400">{it.meeting_title}</p>
                <div className="mt-2.5 flex items-center justify-between text-xs text-slate-500">
                  <span>PIC {it.owner}</span><span className="font-mono">{it.deadline}</span>
                </div>
                <div className="mt-3"><StatusPill status={it.status} /></div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
